// OSI-763: классы ошибок держим рядом с транспортом — они часть его контракта,
// выносить их в отдельный модуль только ради правила max-classes-per-file смысла нет.
/* eslint-disable max-classes-per-file */
import { EventEmitter } from 'eventemitter3';

/**
 * OSI-763: ошибка транспорта NCALayer (нет соединения, NCALayer вернул status=false,
 * соединение закрылось до ответа и т.п.). Отделена от «пользователь отменил»,
 * чтобы вызывающий код показывал разные сообщения.
 */
export class NcaLayerRequestError extends Error {
  constructor(message?: string) {
    super(message || 'NCALAYER_REQUEST_ERROR');
    this.name = 'NcaLayerRequestError';
    Object.setPrototypeOf(this, NcaLayerRequestError.prototype);
  }
}

/**
 * OSI-763: пользователь закрыл окно выбора ключа / отменил ввод пароля.
 * NCALayer отвечает status=true, но без body.result — раньше это молча игнорировалось.
 */
export class NcaLayerCancelledError extends Error {
  constructor() {
    super('NCALAYER_SIGN_CANCELLED');
    this.name = 'NcaLayerCancelledError';
    Object.setPrototypeOf(this, NcaLayerCancelledError.prototype);
  }
}

interface PendingRequest {
  method: string;
  // Заполнены только для промис-вызовов (request). Для legacy-вызовов (emit)
  // ответ уходит в EventEmitter по имени method.
  resolve?: (value: any) => void;
  reject?: (reason?: any) => void;
}

class NCALayer {
  // OSI-763: имя последнего отправленного метода оставлено ТОЛЬКО как fallback для
  // незапрошенных/повторных сообщений (прежнее поведение). Основная маршрутизация —
  // по очереди запросов this.pending, чтобы ответы не перепутались при двух
  // последовательных вызовах sign (фаза challenge + фаза документа).
  private method: string | null = null;

  // OSI-763: FIFO-очередь отправленных и ещё не отвеченных запросов.
  // NCALayer отвечает строго по одному ответу на запрос и в порядке отправки,
  // поэтому первый пришедший ответ принадлежит первому неотвеченному запросу.
  private pending: PendingRequest[] = [];

  constructor(private ws = new WebSocket(''), private ee = new EventEmitter()) {
    this.ws = ws;
    this.ee = ee;
    this.method = null;
    ws.onmessage = this.message.bind(this);
    ws.onopen = this.open.bind(this);
    ws.onclose = this.close.bind(this);
  }

  // Публичный геттер только для чтения: отражает, открыт ли WebSocket в данный момент.
  // Нужен, чтобы подписчик мог проверить актуальное состояние соединения и закрыть гонку,
  // если событие 'connect' (emit в open()) уже произошло до подписки.
  get isOpen(): boolean {
    return this.ws.readyState === WebSocket.OPEN;
  }

  // OSI-763: есть ли неотвеченный запрос. Явная защита от наложения вызовов
  // на стороне потребителя (кнопка «Подписать» блокируется на время подписи).
  get isBusy(): boolean {
    return this.pending.length > 0;
  }

  on(name: string, fn: any) {
    this.ee.on(name, fn);
  }

  off(name: string, fn: any) {
    this.ee.removeListener(name, fn);
  }

  /**
   * Legacy-API: ответ придёт событием с именем метода.
   * Сохранено для существующих потребителей (Signer.tsx, старый путь SignerBasic).
   */
  emit(message: any) {
    const { method } = message;
    this.method = method;
    this.pending.push({ method });
    this.ws.send(JSON.stringify(message));
  }

  /**
   * OSI-763: промис-обёртка над emit. Ответ гарантированно соответствует ЭТОМУ запросу —
   * корреляция по позиции в очереди, а не по имени последнего отправленного метода.
   */
  request(message: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.isOpen) {
        reject(new NcaLayerRequestError('NCALAYER_NOT_CONNECTED'));
        return;
      }

      const pending: PendingRequest = { method: message?.method, resolve, reject };
      this.pending.push(pending);
      this.method = message?.method ?? null;

      try {
        this.ws.send(JSON.stringify(message));
      } catch (err) {
        this.pending = this.pending.filter((item) => item !== pending);
        reject(err instanceof Error ? err : new NcaLayerRequestError(String(err)));
      }
    });
  }

  message(e: any) {
    let result: any;

    try {
      result = JSON.parse(e.data);
    } catch (err) {
      // OSI-763: битый ответ не должен «подвешивать» ожидающий промис.
      const broken = this.pending.shift();
      if (broken?.reject) broken.reject(err);
      else this.ee.emit('error', err);
      return;
    }

    if (result == null) {
      // Прежнее поведение для событийного пути — молчим. Для промис-пути отклоняем,
      // иначе вызывающий код будет ждать вечно.
      if (this.pending[0]?.reject) {
        const empty = this.pending.shift();
        empty?.reject?.(new NcaLayerRequestError('NCALAYER_EMPTY_RESPONSE'));
      }
      return;
    }

    const pending = this.pending.shift();
    if (pending) {
      if (pending.resolve) pending.resolve(result);
      else this.ee.emit(pending.method, result);
      return;
    }

    // Очередь пуста: незапрошенное или повторное сообщение.
    // Сохраняем историческое поведение — маршрутизация по последнему методу.
    this.ee.emit(this.method ? this.method : 'message', result);
  }

  open() {
    this.ee.emit('connect');
  }

  close() {
    // OSI-763: соединение закрылось — все ожидающие промисы нужно отклонить,
    // иначе подписание «зависает» без обратной связи.
    this.rejectPending(new NcaLayerRequestError('NCALAYER_CONNECTION_CLOSED'));
    this.ee.emit('disconnect');
  }

  private rejectPending(error: Error) {
    const { pending } = this;
    this.pending = [];
    pending.forEach((item) => item.reject?.(error));
  }
}

export default NCALayer;
